import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Menu } from '@headlessui/react';
import _ from 'underscore';

import DriverActionCreators from 'actions/DriverActionCreators';
import MarketStore from 'stores/MarketsStore';
import DriverStore from 'stores/DriverStore';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';

import MapView from 'components/shipment/maps/MapView';
import MarkerFactory from 'components/shipment/maps/MarkerFactory';
import DriverInfoWindow from 'components/shipment/maps/DriverInfoWindow';
import truckMarker from 'components/shipment/maps/markers/truck.png';
import workingTruckMarker from 'components/shipment/maps/markers/working-truck.png';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import { useTranslation } from 'react-i18next';
import {
  COUNTRY_CODE_COORDINATE_MAPPING,
  COUNTRY_CODE_DEFAULT_ZOOM_MAPPING,
} from 'constants/CountryCoordinate';

function getDriversFromStores() {
  return DriverStore.getNearbyDrivers();
}

function getMarketsFromStores() {
  return MarketStore.getMarkets();
}

const FleetView = () => {
  const { t } = useTranslation();
  const [markets, setMarkets] = useState({});
  const [markers, setMarkers] = useState([]);
  const pollingIntervalRef = useRef(null);
  const [filters, setFilters] = useState({
    near: COUNTRY_CODE_COORDINATE_MAPPING[COUNTRY_CODE],
    per_page: 500,
    paginate: true,
    distance: 120701, // 75 Miles
    defaultZoom: COUNTRY_CODE_DEFAULT_ZOOM_MAPPING[COUNTRY_CODE],
    page: 1,
  });

  useEffect(() => {
    // on component mount
    MarketsAPIUtils.getMarkets();
    DriverActionCreators.getNearbyDrivers(filters);
    startPolling();
    DriverStore.addChangeListener(onNearbyDriversChange);
    MarketStore.addChangeListener(onMarketChange);

    return () => {
      // on component unmount
      stopPolling();
      DriverStore.removeChangeListener(onNearbyDriversChange);
      MarketStore.removeChangeListener(onMarketChange);
    };
  }, []);

  useEffect(() => {
    DriverActionCreators.getNearbyDrivers(filters);
    resetPolling();
  }, [filters, markets]);

  const onNearbyDriversChange = () => {
    const drivers = getDriversFromStores();
    const nearbyDrivers = drivers.nearbyDrivers;

    const newMarkers = nearbyDrivers.map((driver) => {
      const url =
        driver && driver.has_active_jobs ? workingTruckMarker : truckMarker;
      return MarkerFactory.createMarker(
        driver,
        {
          url,
          scaledSize: new google.maps.Size(50, 50),
        },
        ReactDOMServer.renderToString(
          <DriverInfoWindow
            driver={driver}
            carrierName={
              driver.carrier && driver.carrier.name ? driver.carrier.name : ''
            }
          />,
        ),
      );
    });

    setMarkers(newMarkers);
  };

  const onMarketChange = () => {
    const newMarkets = {};

    _.each(getMarketsFromStores(), (market) => {
      const key = market.name;
      const value = {
        center: market.center,
        code: market.code,
      };
      newMarkets[key] = value;
    });

    setMarkets(newMarkets);
  };

  const resetPolling = useCallback(() => {
    stopPolling();
    startPolling();
  }, [startPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollingIntervalRef.current = setInterval(() => {
      DriverActionCreators.getNearbyDrivers(filters);
    }, 10000);
  }, []);
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const handleMarketChange = (market) => {
    const updatedMarkets = markets;
    const center = updatedMarkets[market].center
      ? updatedMarkets[market].center.coords
      : updatedMarkets['Los Angeles'].center.coords;

    const updatedFilters = Object.assign({ ...filters }, { near: center });
    setFilters(updatedFilters);
    setMarkets(updatedMarkets);
  };

  return (
    <div className="fleet-wrapper" style={{ margin: 0 }}>
      {
        // state.filters.near[0] is longitude
        // state.filters.near[1] is latitude
        // MapView expects latitude first then longitude
        // array.reverse mutates the original array;
        // so the spread operator is used to create a copy
      }
      <MapView
        id="fleet-map"
        width="100%"
        height={1200}
        initCenter={[...filters.near].reverse()}
        initZoom={filters.defaultZoom}
        markers={markers}
      />
      <div className="fleet-tabs">
        {markets && (
          <DropdownMenu title={t('MARKET', { count: 3 })} isWide>
            {Object.keys(markets)
              .sort()
              .map((market) => (
                <Menu.Item
                  id="manage_offer_shorthaul_menu"
                  disabled={false}
                  key={`${market}-dropdown-item`}
                >
                  <DropdownItem onClick={() => handleMarketChange(market)}>
                    {`${market.toUpperCase()} (${markets[market].code})`}
                  </DropdownItem>
                </Menu.Item>
              ))}
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

export default FleetView;
